import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { AlertTriangle, Info } from "lucide-react";

import { Modal } from "@/components/Modal";
import { Pill } from "@/components/Pill";

/**
 * App-wide replacement for `window.confirm` / `window.alert`. Both
 * built-ins draw the host browser's dialog chrome which clashes with
 * the dark, glass-panel look of the rest of the UI (the "OK / Cancel"
 * popover floating over a Backstage page is jarring and breaks the
 * "everything is a Modal" mental model used for rollouts, deletes,
 * etc.). This provider renders the same `Modal` component used by the
 * rest of the app and exposes promise-based imperative hooks so call
 * sites can keep their existing branching logic largely intact:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Detach pool?", tone: "danger" }))) return;
 *
 *   const alert = useAlert();
 *   await alert({ title: "Failed to delete pool", description: err.message });
 *
 * Only one dialog is shown at a time; concurrent calls queue up
 * implicitly because every site `await`s the promise before deciding
 * what to do next, and the few we have don't fan out in parallel.
 */

type Tone = "default" | "danger" | "warn";

interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  /** Optional badge in the header (e.g. "irreversible"). */
  badge?: React.ReactNode;
}

interface AlertOptions {
  title: string;
  description?: React.ReactNode;
  okLabel?: string;
  tone?: Tone;
}

type DialogState =
  | ({
      kind: "confirm";
      resolve: (value: boolean) => void;
    } & ConfirmOptions)
  | ({
      kind: "alert";
      resolve: () => void;
    } & AlertOptions);

interface DialogContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions) => Promise<void>;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function ConfirmDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<DialogState | null>(null);
  // Guard the resolver so a double-click on the footer buttons (or the
  // Esc/backdrop path firing while the action is also clicked) cannot
  // resolve the same promise twice — the second call would be a no-op
  // anyway but throws a warning under StrictMode.
  const settledRef = useRef(false);

  const close = useCallback(() => {
    setState(null);
    settledRef.current = false;
  }, []);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        settledRef.current = false;
        setState({ kind: "confirm", resolve, ...opts });
      }),
    [],
  );

  const alert = useCallback(
    (opts: AlertOptions) =>
      new Promise<void>((resolve) => {
        settledRef.current = false;
        setState({ kind: "alert", resolve, ...opts });
      }),
    [],
  );

  const value = useMemo<DialogContextValue>(
    () => ({ confirm, alert }),
    [confirm, alert],
  );

  const onCancel = useCallback(() => {
    if (!state || settledRef.current) return;
    settledRef.current = true;
    if (state.kind === "confirm") {
      state.resolve(false);
    } else {
      state.resolve();
    }
    close();
  }, [state, close]);

  const onConfirm = useCallback(() => {
    if (!state || settledRef.current) return;
    settledRef.current = true;
    if (state.kind === "confirm") {
      state.resolve(true);
    } else {
      state.resolve();
    }
    close();
  }, [state, close]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <DialogView state={state} onCancel={onCancel} onConfirm={onConfirm} />
    </DialogContext.Provider>
  );
}

function DialogView({
  state,
  onCancel,
  onConfirm,
}: {
  state: DialogState | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const open = state != null;
  const tone: Tone = state?.tone ?? "default";

  const confirmClass =
    tone === "danger"
      ? "btn btn--danger"
      : tone === "warn"
        ? "btn btn--primary"
        : "btn btn--primary";

  const isAlert = state?.kind === "alert";
  const confirmLabel = state
    ? state.kind === "confirm"
      ? (state.confirmLabel ?? "OK")
      : (state.okLabel ?? "OK")
    : "OK";
  const cancelLabel =
    state && state.kind === "confirm" ? (state.cancelLabel ?? "Cancel") : null;

  // Picking an icon for the header so the dialog telegraphs intent at
  // a glance instead of looking like a generic prompt — the modal
  // chrome is the same panel used by larger flows (rollout, delete
  // service), so a tone-coded badge is what differentiates a quick
  // confirm from "you're about to type the slug to delete a service".
  const headerBadge =
    state && "badge" in state && state.badge ? (
      state.badge
    ) : tone === "danger" ? (
      <Pill tone="danger">
        <AlertTriangle
          style={{
            width: 12,
            height: 12,
            verticalAlign: "middle",
            marginRight: 4,
          }}
        />
        {isAlert ? "error" : "danger"}
      </Pill>
    ) : tone === "warn" ? (
      <Pill tone="warn">
        <AlertTriangle
          style={{
            width: 12,
            height: 12,
            verticalAlign: "middle",
            marginRight: 4,
          }}
        />
        warning
      </Pill>
    ) : (
      <Pill tone="neutral">
        <Info
          style={{
            width: 12,
            height: 12,
            verticalAlign: "middle",
            marginRight: 4,
          }}
        />
        confirm
      </Pill>
    );

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="md"
      title={state?.title ?? ""}
      headerExtras={open ? headerBadge : undefined}
      footer={
        <>
          {cancelLabel != null && (
            <button type="button" className="btn" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          <div className="right" />
          <button
            type="button"
            className={confirmClass}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {state?.description != null ? (
        typeof state.description === "string" ? (
          // Preserve `\n` paragraphs from call sites that compose a
          // multi-line message — `<p>` collapses whitespace, so we
          // split on blank lines manually.
          state.description.split(/\n{2,}/).map((para, i) => (
            <p key={i} style={i === 0 ? { marginTop: 0 } : undefined}>
              {para.split("\n").map((line, j, arr) => (
                <span key={j}>
                  {line}
                  {j < arr.length - 1 && <br />}
                </span>
              ))}
            </p>
          ))
        ) : (
          <div style={{ marginTop: 0 }}>{state.description}</div>
        )
      ) : null}
    </Modal>
  );
}

function useDialogContext(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error(
      "useConfirm/useAlert must be used inside <ConfirmDialogProvider>",
    );
  }
  return ctx;
}

/** Returns a function: `(opts) => Promise<boolean>`. */
export function useConfirm() {
  return useDialogContext().confirm;
}

/** Returns a function: `(opts) => Promise<void>`. */
export function useAlert() {
  return useDialogContext().alert;
}
